import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { WIZARD_STORAGE_KEY } from '../constants';
import { usePersistedState } from './persistence';
import { initialWizardState, WizardState } from './types';

export interface WizardStateContextValue {
  state: WizardState;
  setState: (updater: (prev: WizardState) => WizardState) => void;
  reset: () => void;
}

const noop = (): void => undefined;

const WizardStateContext = createContext<WizardStateContextValue>({
  state: initialWizardState,
  setState: noop,
  reset: noop,
});

// Persistence policy: only `rawInput` is persisted to sessionStorage. All
// resolutions, decisions, commit log, and step-unlock state are deliberately
// ephemeral — when an operator returns to a tab, they re-walk the wizard
// from intake so every decision is made against a fresh dedupe preview
// against current BFF state, not a stale snapshot from a previous session.
export const WizardStateProvider = ({
  children,
}: PropsWithChildren<unknown>): JSX.Element => {
  const [persistedRawInput, setPersistedRawInput, clearPersistedRawInput] =
    usePersistedState<string>(WIZARD_STORAGE_KEY, '');

  const [state, setState] = useState<WizardState>(() => ({
    ...initialWizardState,
    rawInput: persistedRawInput,
  }));

  // Mirror rawInput → sessionStorage whenever it changes so a reload picks
  // it up. Step 0 re-parses and re-validates on mount via its own effect.
  useEffect(() => {
    setPersistedRawInput(() => state.rawInput);
  }, [state.rawInput, setPersistedRawInput]);

  const reset = useCallback((): void => {
    clearPersistedRawInput();
    setState(initialWizardState);
  }, [clearPersistedRawInput]);

  const value = useMemo(
    () => ({ state, setState, reset }),
    [state, setState, reset],
  );

  return (
    <WizardStateContext.Provider value={value}>
      {children}
    </WizardStateContext.Provider>
  );
};

export const useWizardState = (): WizardStateContextValue =>
  useContext(WizardStateContext);
